"use client"

import { useEffect, useState } from "react"
import Image from "next/image"

// Post-login splash: a black screen with the BOOTH logo and a bar that fills,
// then clears into the dashboard.
//
// Trigger: the login flow calls markLoginSplash() just before routing to
// the dashboard; <LoginSplash /> (mounted in the dashboard layout) picks
// the flag up, clears it, and plays. The flag carries a timestamp and is
// only honored for a few seconds so it can never fire on a later visit
// if a redirect took the user somewhere other than the dashboard.
//
// Shape of it:
//   bar      the green loader fills empty → full, then holds a beat
//   fading   the overlay clears, dashboard
//
// The "Welcome back" line that used to follow the bar is gone, as is the
// version chip that once sat under it. The bar is the whole message: the same
// loader every page in the app uses, seen once on the way in.
//
// Every animation is compositor-only (transform / opacity) so it stays smooth
// while the dashboard mounts behind it.

const FLAG_KEY = "booth:login-splash"
const FLAG_MAX_AGE_MS = 15_000

const FILL_MS = 1150 // the green travels empty → full
const BAR_MS = 1400 // fill, then hold at full so it's seen arriving
const FADE_MS = 340 // overlay clears
const TOTAL_MS = BAR_MS + FADE_MS

export function markLoginSplash() {
  try {
    sessionStorage.setItem(FLAG_KEY, String(Date.now()))
  } catch {
    // Storage unavailable (private mode quota etc.) — skip the splash.
  }
}

type Stage = null | "bar" | "fading" | "done"

export function LoginSplash() {
  // null = not yet checked (renders nothing, matches SSR output).
  const [stage, setStage] = useState<Stage>(null)
  // The fill is a state-driven transition, not a CSS @keyframes animation, and
  // it flips only after the empty bar has been painted.
  //
  // A keyframes animation starts its clock the moment the element is committed,
  // painted or not. This splash mounts at exactly the moment the dashboard
  // mounts behind it, and on a busy main thread the first frame the user
  // actually sees can land most of the way through that clock — so the bar
  // appeared already full, "it doesn't fill, it just shows green". A transition
  // cannot start until the from-state has rendered, so the travel is always
  // seen from zero however long the mount took.
  const [filled, setFilled] = useState(false)

  useEffect(() => {
    let stamp: string | null = null
    try {
      stamp = sessionStorage.getItem(FLAG_KEY)
      if (stamp) sessionStorage.removeItem(FLAG_KEY)
    } catch {
      // ignore
    }
    const rafs: number[] = []
    const timers: ReturnType<typeof setTimeout>[] = []
    if (!stamp || Date.now() - Number(stamp) > FLAG_MAX_AGE_MS) {
      setStage("done")
      return
    }

    // Reduced motion still gets the beat — the bar just doesn't travel.
    // Cutting it entirely would drop someone straight into a half-mounted
    // dashboard, which is the thing the splash covers.
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches

    setStage("bar")

    // The rest of the sequence is timed from the moment the bar starts moving,
    // NOT from mount. Scheduling it at mount meant a slow dashboard mount spent
    // the bar's budget before the bar had rendered, and the overlay started
    // clearing while the green was still mid-travel.
    const run = () => {
      timers.push(setTimeout(() => setStage("fading"), reduced ? 500 : BAR_MS))
      timers.push(setTimeout(() => setStage("done"), reduced ? 800 : TOTAL_MS))
    }

    if (reduced) {
      setFilled(true)
      run()
    } else {
      // Two frames: the first commits scaleX(0), the second flips it so the
      // browser has a painted origin to transition from.
      rafs.push(
        requestAnimationFrame(() => {
          rafs.push(
            requestAnimationFrame(() => {
              setFilled(true)
              run()
            }),
          )
        }),
      )
    }

    return () => {
      timers.forEach(clearTimeout)
      rafs.forEach(cancelAnimationFrame)
    }
  }, [])

  if (stage === null || stage === "done") return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0C0C0C]"
      style={{
        opacity: stage === "fading" ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease-out`,
      }}
      role="status"
      aria-label="Signing you in"
    >
      <div className="flex flex-col items-center">
        <Image src="/images/booth-logo-white.png" alt="BOOTH" width={120} height={30} priority />

        {/* A white fill on a dim track. It used to be a black fill on a
            white track, which on a black screen read as the bar being eaten
            rather than filling — you saw white vanish, not progress arrive.
            Neutral rather than brand green: the bar reports no status, so it
            shouldn't wear a status colour. Inline so a missing utility can't
            leave the fill invisible. */}
        <div className="mt-6 h-[3px] w-40 overflow-hidden rounded-full bg-white/15">
          <div
            className="h-full w-full rounded-full"
            style={{
              backgroundColor: "#ffffff",
              transformOrigin: "left",
              transform: `scaleX(${filled ? 1 : 0})`,
              transition: `transform ${FILL_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
            }}
          />
        </div>
      </div>

    </div>
  )
}
